import {
  RealtimeClient,
  orchestrator,
  useAuth,
  useCan,
  type EventFrame,
} from "@savvy/runtime";
import { Caps } from "@savvy/shared";
import { Badge, Button, cn } from "@savvy/ui";
import { useEffect, useRef } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { useMonitor } from "./monitor/monitorStore";
import { useLive } from "./state/liveStore";
import { LoginPage } from "./views/LoginPage";

// The debounced agent re-read, reachable from the realtime handler. Module
// scope (not state) — it is plumbing between two effects, not render data.
let agentReadTrigger: (() => void) | null = null;

export function App() {
  const status = useAuth((s) => s.status);
  const restore = useAuth((s) => s.restore);

  // One restore attempt on boot: verify the stored session before trusting it.
  const restored = useRef(false);
  useEffect(() => {
    if (!restored.current) {
      restored.current = true;
      void restore();
    }
  }, [restore]);

  if (status !== "authenticated") return <LoginPage />;
  return <Shell />;
}

function Shell() {
  const api = useAuth((s) => s.api);
  const username = useAuth((s) => s.username);
  const signOut = useAuth((s) => s.signOut);
  const conn = useLive((s) => s.conn);
  const connDetail = useLive((s) => s.connDetail);
  const showAdmin =
    useCan(Caps.TelephonyAgentsManage) || useCan(Caps.TelephonySettingsManage);

  // The realtime connection lives exactly as long as the authenticated shell.
  useEffect(() => {
    const client = new RealtimeClient({
      api,
      handlers: {
        onSnapshot: (f) => useLive.getState().applySnapshot(f),
        onEvent: (f: EventFrame) => {
          useLive.getState().applyEvent(f);
          // An ended call clears any monitor session it was part of — the old
          // console never learned its monitor leg had died.
          if (f.envelope.event_type === "call.ended") {
            const p = f.envelope.payload as { call_uuid?: string };
            if (p.call_uuid) useMonitor.getState().onCallEnded(p.call_uuid);
          }
          // Agent-state-changing events trigger the HTTP re-read: a wrap-up
          // window opens at bridge-agent-end and is often shorter than the
          // 30s poll would ever see.
          if (
            f.envelope.event_type === "queue.bridge-agent-end" ||
            f.envelope.event_type === "queue.agent-status-change"
          ) {
            agentReadTrigger?.();
          }
        },
        onStatus: (c, d) => useLive.getState().setConn(c, d),
      },
    });
    client.start();
    return () => client.stop();
  }, [api]);

  // Agent SWITCH status: a slow re-read keeps the tiles honest between
  // snapshots (30s corrects drift; it is not the realtime path), PLUS an
  // event-driven re-read on the queue events that CHANGE agent state — a call
  // ending starts a wrap-up window that is often shorter than the poll, and
  // status changes (auto-pause included) land as agent-status-change. Both
  // funnel into one debounced read.
  useEffect(() => {
    let stop = false;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const read = async () => {
      try {
        const res = await orchestrator(api).agentStatus();
        if (!stop) useLive.getState().applyAgents(res.agents);
      } catch {
        /* the wallboard shows the realtime conn state; a missed poll is fine */
      }
    };
    agentReadTrigger = () => {
      if (debounce) clearTimeout(debounce);
      debounce = setTimeout(() => void read(), 400);
    };
    void read();
    const t = setInterval(() => void read(), 30_000);
    return () => {
      stop = true;
      clearInterval(t);
      if (debounce) clearTimeout(debounce);
      agentReadTrigger = null;
    };
  }, [api]);

  const navLink = ({ isActive }: { isActive: boolean }) =>
    cn(
      "rounded-md px-3 py-1.5 text-sm no-underline",
      isActive ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:text-foreground",
    );
  const connVariant = conn === "live" ? "success" : conn === "forbidden" ? "danger" : "warning";

  return (
    <div className="flex min-h-screen flex-col">
      <header className="flex items-center gap-3 border-b bg-card px-4 py-2 text-card-foreground">
        <span className="font-bold">Savvy Supervisor</span>
        <nav className="mr-auto flex gap-1">
          <NavLink to="/" end className={navLink}>
            Live
          </NavLink>
          <NavLink to="/history" className={navLink}>
            History
          </NavLink>
          <NavLink to="/analytics" className={navLink}>
            Analytics
          </NavLink>
          <NavLink to="/wallboard" className={navLink}>
            Wallboard
          </NavLink>
          {showAdmin && (
            <NavLink to="/admin" className={navLink}>
              Admin
            </NavLink>
          )}
        </nav>
        <Badge variant={connVariant} title={connDetail ?? ""}>
          {conn}
        </Badge>
        <span className="text-sm text-muted-foreground">{username}</span>
        <Button variant="outline" size="sm" onClick={() => signOut()}>
          Sign out
        </Button>
      </header>
      <main className="mx-auto w-full max-w-6xl p-4">
        <Outlet />
      </main>
    </div>
  );
}
